/**
 * Room / conversation helpers: derive WhatsApp-style conversation summaries,
 * resolve the "other member" of a 1:1, and manage m.direct DM records.
 */
import {
  EventType,
  NotificationCountType,
  Preset,
  type MatrixClient,
  type Room,
  type RoomMember,
} from "matrix-js-sdk";

export interface OtherMember {
  userId: string;
  displayName: string;
  avatarMxc: string | null;
}

export interface ConversationSummary {
  roomId: string;
  title: string;
  other: OtherMember | null;
  lastMessage: string;
  lastActivityTs: number;
  unreadCount: number;
}

const AVATAR_SIZE = 96;

/** Resolve the other member of a 1:1 room (falls back to invited members). */
export function getOtherMember(client: MatrixClient, room: Room): OtherMember | null {
  const me = client.getUserId();
  const joined = room.getJoinedMembers();
  let candidate: RoomMember | undefined = joined.find((m) => m.userId !== me);

  if (!candidate) {
    // No other joined member yet (e.g. invite pending) — look at all members.
    candidate = room
      .getMembers()
      .find((m) => m.userId !== me && (m.membership === "join" || m.membership === "invite"));
  }
  if (!candidate) return null;

  return {
    userId: candidate.userId,
    displayName: candidate.name || candidate.userId,
    avatarMxc: candidate.getMxcAvatarUrl() ?? null,
  };
}

/** Members of a room that are joined or invited. */
export function listRoomMembers(room: Room): OtherMember[] {
  return room
    .getMembers()
    .filter((m) => m.membership === "join" || m.membership === "invite")
    .map((m) => ({
      userId: m.userId,
      displayName: m.name || m.userId,
      avatarMxc: m.getMxcAvatarUrl() ?? null,
    }));
}

/**
 * A room is a "group" (not a 1:1 DM) when it has more than one OTHER member
 * (joined or invited, excluding me), or it carries an explicit name with more
 * than two total members.
 */
export function isGroupRoom(client: MatrixClient, room: Room): boolean {
  const me = client.getUserId();
  const members = listRoomMembers(room);
  const others = members.filter((m) => m.userId !== me);
  if (others.length > 1) return true;
  return Boolean(room.name) && members.length > 2;
}

/** Convert an mxc avatar to an http(s) URL usable in <img>, or null. */
export function avatarHttpUrl(client: MatrixClient, mxc: string | null): string | null {
  if (!mxc) return null;
  // Unauthenticated media (useAuthentication=false) — media is served openly.
  return client.mxcUrlToHttp(mxc, AVATAR_SIZE, AVATAR_SIZE, "crop", false, false, false);
}

/** Human-readable one-line preview from the room's most recent message event. */
function lastMessagePreview(room: Room): { text: string; ts: number } {
  const events = room.getLiveTimeline().getEvents();
  for (let i = events.length - 1; i >= 0; i -= 1) {
    const ev = events[i];
    if (ev.getType() !== "m.room.message") continue;
    const content = ev.getContent();
    const ts = ev.getTs();
    if (content.msgtype === "m.audio") return { text: "🎤 Voice message", ts };
    if (typeof content.body === "string") return { text: content.body, ts };
  }
  const last = events[events.length - 1];
  return { text: "", ts: last?.getTs() ?? 0 };
}

/**
 * Count unread messages deterministically: messages from OTHERS that come
 * after my own read receipt. Server notification counts are unreliable here
 * (push-rule dependent), so we derive it from the timeline + read marker and
 * take the larger of the two.
 */
function computeUnread(client: MatrixClient, room: Room): number {
  const me = client.getUserId();
  if (!me) return 0;
  const events = room.getLiveTimeline().getEvents();
  const readUpTo = room.getEventReadUpTo(me, false);
  let seenMarker = !readUpTo; // no receipt yet → everything from others is unread
  let unread = 0;
  for (const ev of events) {
    if (!seenMarker) {
      if (ev.getId() === readUpTo) seenMarker = true;
      continue;
    }
    if (ev.getType() === "m.room.message" && ev.getSender() !== me) unread += 1;
  }
  const notif = room.getUnreadNotificationCount(NotificationCountType.Total) ?? 0;
  return Math.max(unread, notif);
}

/** Build a conversation summary for the list view. */
export function summarizeRoom(client: MatrixClient, room: Room): ConversationSummary {
  const group = isGroupRoom(client, room);
  const other = group ? null : getOtherMember(client, room);
  const { text, ts } = lastMessagePreview(room);
  const unread = computeUnread(client, room);

  const me = client.getUserId();
  const groupTitle =
    room.name ||
    listRoomMembers(room)
      .filter((m) => m.userId !== me)
      .map((m) => m.displayName)
      .join(", ");

  return {
    roomId: room.roomId,
    title: group ? groupTitle || room.roomId : other?.displayName || room.name || room.roomId,
    other,
    lastMessage: text,
    lastActivityTs: ts,
    unreadCount: unread,
  };
}

/** Joined rooms as summaries, sorted by most-recent activity (desc). */
export function listConversations(client: MatrixClient): ConversationSummary[] {
  return client
    .getRooms()
    .filter((room) => room.getMyMembership() === "join")
    .map((room) => summarizeRoom(client, room))
    .sort((a, b) => b.lastActivityTs - a.lastActivityTs);
}

/** Read the m.direct map ({ userId: roomId[] }) from account data. */
export function getDirectMap(client: MatrixClient): Record<string, string[]> {
  const event = client.getAccountData(EventType.Direct);
  const content = event?.getContent();
  return content && typeof content === "object" ? (content as Record<string, string[]>) : {};
}

/** Find an existing joined DM room with a user, or undefined. */
export function findExistingDm(client: MatrixClient, userId: string): string | undefined {
  const map = getDirectMap(client);
  const roomIds = map[userId] ?? [];
  return roomIds.find((id) => client.getRoom(id)?.getMyMembership() === "join");
}

/** Immutably add a room to the m.direct record for a user and persist it. */
export async function recordDirectRoom(
  client: MatrixClient,
  userId: string,
  roomId: string,
): Promise<void> {
  const map = getDirectMap(client);
  const existing = map[userId] ?? [];
  if (existing.includes(roomId)) return;
  const next = { ...map, [userId]: [...existing, roomId] };
  await client.setAccountData(EventType.Direct, next);
}

/**
 * Start (or reuse) a 1:1 DM with `userId`. Returns the room id.
 * If a joined DM already exists it is reused; otherwise a new
 * trusted_private_chat room is created, invited, and recorded in m.direct.
 */
export async function startDirectMessage(client: MatrixClient, userId: string): Promise<string> {
  const existing = findExistingDm(client, userId);
  if (existing) return existing;

  const { room_id: roomId } = await client.createRoom({
    preset: Preset.TrustedPrivateChat,
    is_direct: true,
    invite: [userId],
  });

  await recordDirectRoom(client, userId, roomId);
  return roomId;
}
